import React from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Baby,
  HeartPulse,
  Building2,
  Stethoscope,
  ShieldAlert,
} from "lucide-react";

const services = [
  {
    icon: ShieldAlert,
    title: "Services d'urgences",
    desc: "Décision rapide face à un patient fébrile ou présentant une suspicion d'infection aiguë, dès l'arrivée.",
  },
  {
    icon: Baby,
    title: "Pédiatrie",
    desc: "Aide à la distinction bactérien / viral chez l'enfant et soutien au bon usage des antibiotiques.",
  },
  {
    icon: HeartPulse,
    title: "Gériatrie",
    desc: "Information complémentaire fiable dans des présentations cliniques souvent atypiques.",
  },
  {
    icon: Building2,
    title: "Soins non programmés",
    desc: "Résultat directement au point de prise en charge pour fluidifier le parcours patient.",
  },
  {
    icon: Stethoscope,
    title: "Biologie délocalisée (POC)",
    desc: "Adapté aux nouvelles organisations de soins centrées sur le patient et la décision rapide.",
  },
  {
    icon: Activity,
    title: "Antibiotic Stewardship",
    desc: "Levier concret pour réduire les prescriptions antibiotiques inutiles et lutter contre l'antibiorésistance.",
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-2xl mb-16 nx-fade">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium mb-4">
            Applications cliniques
          </div>
          <h2
            data-testid="services-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight"
          >
            Une décision plus rapide,{" "}
            <span className="font-semibold">là où chaque minute compte.</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            MeMed Key® et MeMed BV® s&apos;intègrent simplement dans les
            organisations de soins, de l&apos;accueil des urgences aux centres
            de soins non programmés, jusqu&apos;aux laboratoires de biologie
            délocalisée.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[#0E1530] border border-white/10 rounded-2xl p-8 transition-all duration-300 hover:border-[#5BC4D1]/40 hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(91,196,209,0.08)]"
              >
                <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1] mb-6 group-hover:bg-[#5BC4D1]/20 transition-colors">
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-medium text-white">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  {s.desc}
                </p>
                <div className="mt-6 text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2 group-hover:text-[#5BC4D1] transition-colors">
                  En savoir plus
                  <span className="h-px w-6 bg-current" />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
